import { useEffect, useState } from 'react';
import {useRecoilState, useRecoilValue} from 'recoil';
import { beaconSelector } from '../recoil/selector';
import useBeacon, { IClkBtnOptionProperties } from '../hooks/useBeacon';
import { PAGE_NM } from '../types/types';
import {fetchSelectLDongList} from "../../infra/api/mtg/fetchSelectLDongList";
import * as Atom from "../recoil";
import {fetchSelectApartmentList} from "../../infra/api/mtg/fetchSelectApartmentList";
import useToast from "../hooks/useToast";
import {fetchSelectApartmentPrice} from "../../infra/api/mtg/fetchSelectApartmentPrice";
import {currencyKR} from "../helper/filter";

export const useMtgSelect = () => {
  // beacon
  const beaconData = useRecoilValue(beaconSelector);
  const beacon = useBeacon({
    mediaCd: beaconData.mediaCd,
    custSeq: beaconData.custSeq,
    srcCd: beaconData.srcCd,
    pageName: PAGE_NM.MTG_SELECT,
  });
  
  const sendBeaconClkBtnOption = (
      {btmsheet_name, option_name, before_option_name,
  }: IClkBtnOptionProperties) => {
    beacon.sendClkBtnOption({
      properties: <IClkBtnOptionProperties>{
        btmsheet_name: btmsheet_name,
        option_name: option_name,
        before_option_name: before_option_name,
      },
    });
  };
  

  const sendBeaconClkBtnUnkAddressDtl = () => {
    beacon.sendClkBtnUnkAddressDtl();
  };

  const sendBeaconSearch = (keyword: string, subEventName: string, sheetName: string, success: boolean, count: number) => {
    beacon.sendSearchEvent(
      {
        events: {
          subEventName: subEventName
        },
        properties: {
          btmsheet_name: sheetName, 
          search_keyword: keyword,
          success: success,
          count: count,
        }
      }
    )
  }

  const sendBeaconClkBtn = (subEventName: string, props: any) => {
    beacon.sendClkBtn(
      {
        events: {
          subEventName: subEventName,
        }, 
        properties: props
      }
    );
  }

  useEffect(() => {
    beacon.sendViewPage();
  }, []);
  const [isActive, setActive] = useState<boolean>(false);

  // get ldongCode
  const [personal] = useRecoilState(Atom.personalInfo);
  const getLdongList = async (ldongCode?: string) => {
    const response = await fetchSelectLDongList({
      trackCd: personal.trackCd,
      transactionNo: personal.transactionNo,
      authToken: personal.authToken,
      condition: {ldongCode: ldongCode, regionName: '', rangeCd: ''}
    });
    return response.data.result.list.map((item:any) => {
      return {
        text: item.regionName,
        value: item.ldongCode,
        fullNm: item.regionNameAbbr,
        pldongCode: item.pldongCode
      }
    });
  }

  const [housePriceWarning, setHousePriceWarning] = useState<boolean>(false);
  const [hopefulAmtWarning, setHopefulAmtWarning] = useState<boolean>(false);

  // get apartment
  const { addToast } = useToast();
  const [mortgageInfo] = useRecoilState(Atom.mortgageInfo);
  const searchApartment = async (searchText: string, ldongCode?: string) => {
    if (!searchText && !ldongCode) return;
    const response = await fetchSelectApartmentList({
        trackCd: personal.trackCd,
        transactionNo: personal.transactionNo,
        authToken: personal.authToken,
        condition: {
          ldongCode: (searchText === ''  && ldongCode) || '', // 법정동 코드
          apartmentName: '', // 물건 이름
          buildingType: mortgageInfo.houseType.value, // 물건 유형 (아파트/오피스텔)
          searchText: searchText // 주소 + 물건명 복합 검색 텍스트
        },
    });

    const res = response.data;
    sendBeaconSearch(searchText, "house", "주택 주소", res.isSucceed, res?.result?.list?.length ?? 0);

    if (!res.isSucceed && res.resultMessage) {
      addToast(res.resultMessage);
      return;
    }
    if(ldongCode) {
      return res?.result?.list.map((item:any) => {
        return {
          text: item.apartmentName,
          value: item.apartment,
        }
      });
    }else {
      return {
        list: res?.result?.list?.map((item:any, idx:number) => {
          return {
            type:'b',
            item: {
              name: item.apartmentName,
              address: item.areaRegion,
              code: item.apartment,
              type: 'b',
              isRegulation: item.isRegulation,
              ldongCode: item.ldongCode,
            }
          }
        })
      }
    }
  };

  const convertToPyoung = (dedicatedArea: string) => {
    if (!dedicatedArea)
      return "0";
    
    const index = dedicatedArea.search(/[^0-9]/g);
    const num = index > 0 ? Number(dedicatedArea.substring(0, index)) : Number(dedicatedArea.replace(/[^0-9\.]/g, ''));
    if (isNaN(num))
      return "0";
    
    return (num / 3.3).toFixed(0);
  }

  const searchRateSeq = async (apartment: string) => {
    const response = await fetchSelectApartmentPrice({
      trackCd: personal.trackCd,
      transactionNo: personal.transactionNo,
      authToken: personal.authToken,
      condition: {
        apartment: apartment // 물건 코드
      },
    })
    const list = response.data.result.list.map((item:any) => {
      const pyoung = convertToPyoung(item.supplyArea);
      return {
        supplySize: pyoung,
        text: `${pyoung}평/${convertToPyoung(item.dedicatedArea)}평 (${item.supplyArea}/${item.dedicatedArea})`, // 면적
        value: item.rateSeq, //물건 상세 순번
        desc: currencyKR(item.saleGeneralAverage), // 평균 시세
        amt: item.saleGeneralAverage / 10000, // 시세
        address: item.areaRegion // 주소
      }
    });

    return list.sort((a: any, b: any) => a.supplySize - b.supplySize);
  }

  useEffect(() => {
    if (mortgageInfo.houseType?.value && 
          mortgageInfo.ldongCode?.value && 
          (mortgageInfo.housePrice || mortgageInfo.rateSeq?.value) && 
          mortgageInfo.hopefulAmt
          ) {
        const hopefulAmt = Number(mortgageInfo.hopefulAmt);
        const housePrice = Number(mortgageInfo.housePrice);
        if (!isNaN(housePrice) && housePrice > 0 && hopefulAmt > housePrice) {
          setHopefulAmtWarning(true);
          setActive(false);
          return;
        }
        setHopefulAmtWarning(false);
        if (mortgageInfo.houseType.value === '10' || mortgageInfo.houseType.value === '20') {
          if ((mortgageInfo.dong && mortgageInfo.ho) || mortgageInfo.unkAddress === 'Y') {
            setActive(true);  
          } else {
            setActive(false);  
          }
        } else {
          setActive(true);
        }
    } else {
      setActive(false);
    }
    
  }, [mortgageInfo]);

  return {
    housePriceWarning,
    hopefulAmtWarning,
    setHousePriceWarning,
    setHopefulAmtWarning,
    isActive,
    sendBeaconClkBtnOption,
    sendBeaconClkBtnUnkAddressDtl,
    sendBeaconClkBtn,
    getLdongList,
    searchApartment,
    searchRateSeq
  };
};